import { AiConsentState } from '../attestations/ai-consent.types';
import { KsAssessmentFields } from './assessment-record.types';

// Mirrors flow_ai.kickstart serializers.  Decimal fields arrive as strings.

import { BadgeVariant, StatusMeta } from '../status/status-meta.types';
import { DocAssessStatus } from '../status/doc-assess.types';
import { RiskSummary, LatestAiAdvisory, LatestAiAttempt, OnboardingProgress } from '../df/df.types';
import { KsRecoveryFlag } from '../ops/ops-recovery.types';
import { SizingHold } from '../applications/sizing-hold.types';
import { LookupListVersions } from './lookup-lists.types';

export type { KsRecoveryFlag };

// Re-export convenience aliases so callers can import from one place.
export type { BadgeVariant, StatusMeta };
export type { RiskSummary, LatestAiAdvisory, OnboardingProgress } from '../df/df.types';

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

type KsStatus = 'NONE' | 'PENDING' | 'ACTIVE' | 'REPAID';

export type KsApplicationStage = 'DRAFT' | 'SUBMITTED' | 'MORE_INFO_REQUIRED' | 'DECLINED';

export type KsDocumentType = 'BANK_STATEMENT' | 'FINANCIALS' | 'ID_DOCUMENT' | 'SURETY' | 'OTHER';

type KsDecisionAction = 'approve' | 'decline' | 'refer';

/**
 * FLOW-402 (DOC-89-A §5): the one stage row — *Submitted → Being assessed →
 * Offer → Signed → Paid out* — as the back end reads it from the record
 * (`kickstart/stage.py`). `reached` is null on a draft; `label` is the reached
 * stage's name, or the record's own word before any stage. The screen draws
 * the steps as given and works out none of them.
 */
export type KsStageKey = 'SUBMITTED' | 'BEING_ASSESSED' | 'OFFER' | 'SIGNED' | 'PAID_OUT';

interface KsStageStep {
  key: KsStageKey;
  label: string;
  state: 'passed' | 'reached' | 'ahead';
}

/** DOC-88 UI-DA-88-07 — the three stages between acceptance and payout. */
interface KsPathStage {
  key: 'PREPARING_DOCUMENTS' | 'SENT_FOR_SIGNING' | 'SIGNED_AWAITING_PAYOUT';
  label: string;
}

export interface KsStageRow {
  reached: KsStageKey | null;
  label: string;
  path?: KsPathStage | null;
  steps: KsStageStep[];
}

// ---------------------------------------------------------------------------
// Sub-objects
// ---------------------------------------------------------------------------

export interface KsDocument {
  short_id: string;
  doc_type: KsDocumentType;
  /**
   * FLOW-243: the declared account this statement was attributed to, as the
   * `BankAccount` short_id — `null` when nobody has attributed it. A short_id
   * and not a nested account: the screen already holds the declared-account
   * list and resolves against it.
   */
  source_account_ref?: string | null;
  label: string;
  file_name: string;
  content_type: string;
  created_at: string;
  // Per-upload AI quality check. Document-level only (not the application's
  // advisory recommendation, which is Ops-only — N4).
  assess_status: DocAssessStatus;
  assess_message: string;
}

interface KsStatusEvent {
  field: string;
  from_status: string;
  to_status: string;
  actor_name: string | null;
  note: string | null;
  created_at: string;
}

interface KsReviewNote {
  short_id: string;
  body: string;
  author_name: string;
  created_at: string;
}

export interface AiRecommendation {
  recommendation: string;
  summary: string;
  confidence: number;
  risk_flags?: string[];
  missing_items?: string[];
}

// ---------------------------------------------------------------------------
// Application shapes
// ---------------------------------------------------------------------------

/**
 * Whether `amount_requested` is the client's own figure. A draft minted by Ops's
 * Proceed (or the indicative-sizing request) carries the R50,000 placeholder and
 * no answer to the financials question; the figure becomes the client's only once
 * Details have been confirmed, which the required question's boolean records.
 * Screens show no amount before that — a placeholder is not an ask.
 */
export function hasConfirmedAmount(
  app: Pick<KsApplication, 'application_stage' | 'has_financials_declared'>,
): boolean {
  return app.application_stage !== 'DRAFT' || app.has_financials_declared !== null;
}

export interface KsApplication {
  short_id: string;
  entity_short_id: string;
  entity_name: string;
  amount_requested: string;
  purpose: string | null;
  /** Who at CapX is dealing with it; '' when nobody is (Henk, 2026-10-23). */
  dealt_with_by: string;
  ks_status: KsStatus;
  ks_status_display: string;
  application_stage: KsApplicationStage;
  application_stage_display: string;
  // FLOW-402 (DOC-89-A §5) — the stage row, from the record.
  stage_row: KsStageRow;
  // BRE-200 Screen 6.2 progress (client-safe; N4). Present on both list and
  // detail — the client serializer serves both.
  onboarding_progress: OnboardingProgress;
  // BRE-200 KS-OB-06/07/08 client confirmations (FLOW-163). The client's own
  // actions, so reflecting them back is N4-safe; the `_by` companions are
  // Ops-only and deliberately absent here.
  loan_agreement_signed: boolean;
  loan_agreement_signed_at: string | null;
  debit_order_confirmed: boolean;
  debit_order_confirmed_at: string | null;
  payout_account_confirmed: boolean;
  payout_account_confirmed_at: string | null;
  // §B11.1 repayment frequency — the RULE's (FLOW-305), set when the offer is
  // issued. Empty until then. Never the client's pick.
  repayment_frequency: KsRepaymentFrequency | '';
  // §B11.1g — what the client would prefer. A preference the person deciding
  // sees, never a choice.
  repayment_frequency_preference: KsRepaymentFrequency | '';
  // FLOW-303 — the current offer (Screen 5.0 / 5.1); null before one is issued.
  offer: KsOffer | null;
  // FLOW-224 (DOC-78) — the provisional first look. ABSENT while the
  // publication switch is off, null where no publishable figure exists.
  first_look?: KsFirstLook | null;
  // FLOW-125 — what Ops asked for when they bounced this back, in their own
  // words. Empty string when nothing has been requested. Note this is the
  // client-facing copy, NOT the internal status-event note (which never reaches
  // a client serializer).
  client_info_request_message: string;
  submitted_at: string | null;
  activated_at: string | null;
  // FLOW-119 — null until the facility is activated.
  facility: KsFacility | null;
  /**
   * FLOW-290 — AI-processing consent state, so the screen can offer the
   * withdrawal it promises. `null` = never asked (not the same as withdrawn).
   */
  ai_consent: AiConsentState | null;
  /**
   * FLOW-331 (DOC-72 step 1) — the client's answer on having financial
   * statements; `null` = not answered. Sets the months asked for, never the
   * route (that stays system-set at submit, and Ops-only).
   */
  has_financials_declared: boolean | null;
  /** FLOW-331 — the every-account question as last answered; null = not yet. */
  account_confirmation: KsAccountConfirmation | null;
  /**
   * FLOW-366 (DOC-86) — the business confirms its own account list once the
   * offer is accepted; null before then. The back end decides whether it is
   * owed and whether this viewer may answer.
   */
  business_account_confirmation: KsBusinessAccountConfirmation | null;
  /**
   * FLOW-367 (DOC-68-B UI-DA-68-B-06) — the client's signing lines: the state, the
   * date sent and route, the signature date. The agreement is signed outside Flow.
   */
  signing_status: KsSigningStatus;
  /** FLOW-331 — *Tell us about your business*: client context, never evidence. */
  client_context: KsClientContextEntry[];
  /** DOC-73 UI-DA-73-04 — the block takes entries until an offer is issued; the back end's call. */
  client_context_open: boolean;
  /**
   * FLOW-326 (DOC-73) — what is still outstanding after submission, each item
   * with who owes it, in the back end's words. Empty outside the window
   * (submitted, no offer yet). The screen infers nothing from anything else.
   */
  outstanding: KsOutstandingItem[];
  /** FLOW-352 — every tax compliance PIN sent, newest first. */
  tax_pins: KsTaxPin[];
  /** FLOW-352 — a PIN may be sent now, a later one too; the back end's call. */
  tax_pin_open: boolean;
  /** FLOW-326 — the progress line's position; null = say nothing. */
  position: KsPosition | null;
  created_at: string;
  updated_at: string;
}

type KsOutstandingOwner = 'BUSINESS' | 'CAPX';

/**
 * The act the page offers beside a business-owed item (DOC-73 §2) — rule acts
 * only (FLOW-381, DOC-89 §5): no document is an item. UPLOAD_BANK_STATEMENT is
 * a named account's statements, part of the account confirmation.
 */
type KsOutstandingAct =
  'UPLOAD_BANK_STATEMENT' | 'ACCOUNT_CONFIRMATION' | 'CONSENT' | 'TAX_PIN' | 'NONE';

export interface KsOutstandingItem {
  key: string;
  owner: KsOutstandingOwner;
  text: string;
  /** When it was asked for; null on CapX's items (no date, no countdown). */
  asked_at: string | null;
  act: KsOutstandingAct;
  ref: string;
}

/**
 * FLOW-352 (DOC-83 §2) — a SARS Tax Compliance Status PIN as the business's
 * page reads it: who sent it and when, never the PIN itself.
 */
export interface KsTaxPin {
  short_id: string;
  given_by_name: string;
  /** The introducing agent sent it for the business (`FC-DET-065`'s pattern). */
  for_the_business: boolean;
  given_at: string;
}

/** DOC-83 §3 — the PIN on Ops's tax line, for Ops to check on eFiling. */
export interface KsTaxPinOps extends KsTaxPin {
  pin: string;
}

/** UI-DA-73-03's four positions. */
export type KsPosition = 'BUSINESS_NEEDED' | 'CAPX_WORKING' | 'PRE_ASSESSMENT_EXISTS' | 'HELD';

export type KsAccountConfirmationAnswer = 'ALL_UPLOADED' | 'NOT_ALL';

/** One account named as not uploaded (DOC-72 UI-DA-72-04). */
export interface KsNamedAccount {
  bank_name: string;
  /** Four digits, or '' when not known. */
  last_four: string;
  reason: string;
}

export interface KsAccountConfirmation {
  answer: KsAccountConfirmationAnswer;
  answered_by_name: string;
  answered_as: 'MEMBER' | 'AGENT';
  created_at: string;
  named_accounts: KsNamedAccount[];
}

/** One account on the derived list (`FC-DET-047`) as the record holds it. */
interface KsListedAccount {
  bank_name: string;
  last_four: string;
  /** Statements are on file; otherwise it was named, with `reason`. */
  on_file: boolean;
  reason: string;
}

/** FLOW-366 (DOC-86 UI-DA-86-02/03/04). */
export interface KsBusinessAccountConfirmation {
  /** An agent gave the form's answer and no one at the business has since. */
  owed: boolean;
  /** The viewer is a person at the business — never an agent (UI-DA-86-04). */
  may_answer: boolean;
  accounts: KsListedAccount[];
}

/** One context entry — the business's own words or document, unverified. */
export interface KsClientContextEntry {
  short_id: string;
  source: 'CLIENT';
  source_display: string;
  text: string;
  file_name: string | null;
  file_short_id: string | null;
  entered_by_name: string;
  entered_as: 'MEMBER' | 'AGENT';
  created_at: string;
}

/**
 * One *Added by CapX* row on the Ops Context panel (DOC-74, FLOW-327). Ops-only.
 * A BUSINESS_RECEIVED row carries the business's part (`received_how`,
 * `received_at`) beside Ops's part (`author_name`, `created_at`) — never merged.
 */
type KsOpsContextSource = 'BUSINESS_RECEIVED' | 'CAPX_OWN';

interface KsOpsContextEntry {
  short_id: string;
  source: KsOpsContextSource;
  source_display: string;
  text: string;
  file_name: string | null;
  author_name: string;
  created_at: string;
  received_how: string;
  received_at: string | null;
  obtained_from: string;
  /** Short id of the earlier entry this one corrects. */
  corrects: string | null;
  /** A review note written before DOC-74 — shown as CapX's own, never correctable by id. */
  from_review_notes: boolean;
}

/** BRE-056 §3: 8 months either way — only the instalment cadence differs. */
type KsRepaymentFrequency = 'WEEKLY' | 'MONTHLY';

// ---------------------------------------------------------------------------
// FLOW-303 — the Kickstart offer (`§A3.2.1`, DOC-62)
// ---------------------------------------------------------------------------

/** LAPSED is computed by the back end, never stored; it is not a decline. */
type KsOfferState = 'OPEN' | 'ACCEPTED' | 'LAPSED' | 'SUPERSEDED' | 'WITHDRAWN';

/** `FC-DET-073` — which kind of date the first debit is. Picks the sentence. */
type KsFirstDebitKind = 'CALCULATED' | 'OPS_SET';

/** The seven figures, exactly as the offer record states them. */
export interface KsOfferFigures {
  approved_amount: string;
  facility_fee_amount: string;
  /** Percentage, e.g. "28.00". */
  fee_percent: string;
  total_repayable: string;
  instalment_amount: string;
  repayment_frequency: KsRepaymentFrequency;
  instalment_count: number;
  term_months: number;
  first_debit_date: string;
  first_debit_kind: KsFirstDebitKind;
  /** MONTHLY only — weekly debits fall on the 7th, 14th, 21st and last day. */
  debit_day: number | null;
}

export interface KsOffer extends KsOfferFigures {
  version: number;
  state: KsOfferState;
  issued_at: string;
  /** = issued_at: an offer is shown when Ops issues it (`FC-DET-086`). */
  shown_at: string | null;
  /** issued_at + 7 calendar days, fixed at issue by the back end. */
  expires_at: string | null;
  accepted_at: string | null;
  accepted_by_name: string | null;
  accepted_capacity: string;
  /** The version this one replaced by a change of terms (old beside new). */
  previous: (KsOfferFigures & { version: number; was_accepted: boolean }) | null;
  monthly_request_pending: boolean;
}

interface KsCadenceMonth {
  month: string;
  days_cleared_count: number;
  days_cleared: number[];
  best_day: number;
  best_day_headroom: string;
  weekly_dates_cleared: Record<string, boolean>;
}

/** `§B11.1b`/`c` — the outcome and what the person is shown at a referral. */
export interface KsCadenceEvidence {
  outcome: 'MONTHLY' | 'WEEKLY' | 'REFER';
  determinable: boolean;
  debit_day: number | null;
  reason: string;
  monthly_claim: string;
  weekly_claim: string;
  overdraft_limit: string;
  overdraft_source: 'PRINTED' | 'ESTIMATED';
  months: KsCadenceMonth[];
  /** FLOW-358 — every month a statement on file covers, testable or not (§B11.1d's options). */
  statement_months?: string[];
  candidate_days: number[];
  notes: string[];
  assessed_account: string;
  client_preference: KsRepaymentFrequency | '';
  top_up: { count: number; value: string; share_of_credits_pct: string | null; basis: string };
}

export interface KsOfferOps extends KsOffer {
  issued_by_name: string | null;
  change_reason: string;
  /** FLOW-405 (DOC-82-A §3): the Committee's answer this version was issued
   * on — the ground, by its short id in `committee_referrals`; null if none. */
  committee_referral: string | null;
  pricing_tier: string;
  frequency_basis: 'SYSTEM' | 'REFERRAL' | 'CLIENT_REQUEST';
  cadence_evidence: Partial<KsCadenceEvidence>;
  superseded_at: string | null;
  withdrawn_at: string | null;
  withdrawn_by_name: string | null;
  withdraw_reason: string;
}

export type KsCadenceDecider =
  | 'CARRIES_MOST_DAYS'
  | 'AFTER_RECURRING_RECEIPT'
  | 'GOOD_DAYS_MOVE'
  | 'TOPPED_UP'
  | 'CLIENT_ASKED'
  | 'NOT_LISTED';

interface KsCadenceReferral {
  short_id: string;
  cadence: KsRepaymentFrequency;
  debit_day: number | null;
  deciders: KsCadenceDecider[];
  months_relied_on: string[];
  reason: string;
  cc_ref: string;
  decided_by_name: string | null;
  decided_at: string;
}

/** FLOW-405 (DOC-82-A §2): the three answers a member may give on a referral. */
export type KsCommitteeAnswer = 'PROCEED' | 'AMOUNT' | 'END';

/**
 * FLOW-351 (DOC-82 §6): a referral to the Credit Committee — who, when, the
 * reason — and, once answered (FLOW-405, DOC-82-A §2), which act, the member
 * who took it, when, their reason and any amount approved.
 */
export interface KsCommitteeReferral {
  short_id: string;
  referred_by_name: string | null;
  referred_at: string;
  reason: string;
  is_open: boolean;
  answer: KsCommitteeAnswer | '';
  answered_by_name: string | null;
  answered_at: string | null;
  answer_reason: string;
  answer_amount: string | null;
}

/** The member's own act on the open referral — `…/committee-answer/`. */
export interface KsCommitteeAnswerPayload {
  answer: KsCommitteeAnswer;
  reason: string;
  amount?: string;
}

/** DOC-82 §2: the five states, the record's four plus the hold. */
type KsAmountState = 'NOT_ASSESSED' | 'HELD' | 'REFUSED' | 'NOT_VIABLE' | 'SIZED';

/** `§B7`'s two Credit Committee triggers, each naming itself. */
type KsCommitteeTrigger = 'ABOVE_RECOMMENDATION' | 'ABOVE_APPLIED_FOR';

/** DOC-82 §5: the endings a decline records. COMMITTEE is written by the
 * member's own *End the application* act, never offered to Ops (DOC-82-A §4). */
export type KsDeclineGround = 'BELOW_FLOOR' | 'HARD_DECLINE' | 'COMMITTEE';

interface KsDeclineGroundOption {
  ground: KsDeclineGround;
  text: string;
  gates?: { gate: string; note: string }[];
}

/**
 * FLOW-351 (DOC-82 §2): the *Amount* section's record. Every figure, state,
 * trigger and ground is the back end's — the screen computes nothing
 * (UI-DA-54-A-01). `starting_figure` is null on anything but SIZED: a refused
 * sizing is not a ceiling of zero (FC-DET-031).
 */
export interface KsAmountReview {
  applied_for: string;
  state: KsAmountState;
  reason: string;
  recommended: string | null;
  /** DOC-82 §4: what the amount field opens with — the recommendation, capped
   * at the request (above it only a Committee member sets the amount). */
  prefill: string | null;
  starting_figure: string | null;
  /** FC-DET-016's second limb — the record's flag, never the screen's sum. */
  excess: string | null;
  hard_decline: { gates: { gate: string; note: string }[]; as_at: string } | null;
  /** What the model reported FAILED: a prompt for a person, never a decline. */
  gates_reported: { gate: string; note: string }[];
  open_referral: string | null;
  /** FLOW-405 (DOC-82-A §3): the Committee's standing answer — the newest
   * answered referral's short id — and the amount a member approved above the
   * recommendation, shown locked in the field; null where there is none. */
  committee_answer: string | null;
  locked_amount: string | null;
  decline_grounds: KsDeclineGroundOption[];
  trigger_words: Record<KsCommitteeTrigger, string>;
}

/** DOC-82-A §3–§4: what the system says of an amount Ops is about to approve:
 * the limit it breaks; which trigger only a member may cross; whether Ops may
 * issue it (within both limits, or exactly the member's figure); and the
 * new-referral line for an amount between the recommendation and that figure. */
export interface KsAmountCheck {
  refusal: string;
  triggers: { code: KsCommitteeTrigger; text: string }[];
  /** The plain name of each limit crossed, for the member's own line (§2). */
  limits: string[];
  authorised: boolean;
  line: string;
}

export interface KsFrequencyRequest {
  short_id: string;
  offer_version: number;
  reason: string;
  requested_by_name: string | null;
  created_at: string;
  answered_at: string | null;
  answered_by_name: string | null;
  agreed: boolean | null;
  answer_reason: string;
  monthly_debit_day: number | null;
}

/** DOC-62 §8.4 — an outstanding activation condition and who owes it. */
interface KsActivationBlocker {
  condition: string;
  label: string;
  owed_by: string;
}

export interface KsOfferActionPayload {
  action: 'change_terms' | 'withdraw' | 'issue_fresh';
  reason?: string;
  approved_amount?: string;
  first_debit_date?: string;
  debit_day?: number | null;
}

export interface KsCadenceReferralPayload {
  cadence: KsRepaymentFrequency;
  debit_day: number | null;
  deciders: KsCadenceDecider[];
  months_relied_on: string[];
  reason: string;
  cc_ref?: string;
  evidence?: Partial<KsCadenceEvidence>;
}

// ---------------------------------------------------------------------------
// FLOW-368 item 5 — *Before payout* (DOC-87, -A, -B). Ops only.
// ---------------------------------------------------------------------------

type KsBeforePayoutState = 'MET' | 'WAIVED' | 'UNMET' | 'BLOCKED' | 'NOT_RECORDED';

/** One row of *Before payout*: the record's state for one condition, who set it and when. */
export interface KsBeforePayoutRow {
  condition: string;
  actor: 'Ops' | 'Client' | 'System + Ops';
  state: KsBeforePayoutState;
  /** The record's words for the state line: *Not recorded*, *Signed 12 Oct 2026*, … */
  detail: string;
  by: string;
  at: string | null;
  /** A second line from a related record (the tax PIN; why a row is not counted). */
  extra: string;
  /** The back end counts the row in *complete*; false where the check is not yet enforced. */
  counted: boolean;
  waivable: boolean;
  not_waivable_reason: string;
  scope: string;
  /** The scope, shown not chosen: *this company* · *this loan only* · … */
  scope_label: string;
  waiver: { by: string; at: string; tier: string; scope_label: string; reason: string } | null;
  /** §A3.7's reset line, or ''. */
  reset: string;
  /** `KS-OB-10` only (DOC-87-A): the act, its choices and the debit order's account. */
  act?: KsDebitAccountAct;
  debit_account?: string;
}

/** One account the assessment was taken from, as its statements show it. */
interface KsAssessedAccountChoice {
  short_id: string;
  /** *FNB ····9126* */
  label: string;
  statements: number;
  period_start: string | null;
  period_end: string | null;
}

/**
 * DOC-87-A UI-DA-87-A-01: whether *Confirm which assessed account* is offered. When it is
 * not, `reason` is the record's waiting line; nothing is ever pre-selected.
 */
export interface KsDebitAccountAct {
  available: boolean;
  reason: string;
  debit_account: { bank_name: string; last_four: string; label: string } | null;
  choices: KsAssessedAccountChoice[];
}

/** One assessed account's short id, or `none_of_these` — never both, never neither. */
export interface KsDebitAccountCheckPayload {
  assessed_account?: string;
  none_of_these?: boolean;
}

/** The mandate confirmation's account (DOC-88 UI-DA-88-01): the bank and the last four. */
export interface KsDebitOrderAccountPayload {
  bank_name: string;
  last_four: string;
}

interface KsLedgerPayment {
  amount: string;
  received_at: string;
  reference: string;
  recorded_by: string;
}

export interface KsLedgerInstalment {
  short_id: string;
  sequence: number;
  due_date: string;
  amount: string;
  received: string;
  settled: boolean;
  payments: KsLedgerPayment[];
}

/** The Ops repayment screen's record — ACTIVE loans only, Ops-only, no arrears state. */
export interface KsRepaymentLedger {
  instalments: KsLedgerInstalment[];
  all_settled: boolean;
}

export interface KsRecordRepaymentPayload {
  instalment_short_id: string;
  amount: string;
  received_at: string;
  reference?: string;
}

interface KsBeforePayoutLine {
  key: 'sureties' | 'business_confirmation' | 'disclosure';
  label: string;
  text: string;
}

/** UI-DA-87-06: every figure from the record; the screen computes none. */
interface KsActivationConfirmation {
  amount: string;
  instalment_amount: string;
  first_debit_date: string;
  frequency: KsRepaymentFrequency;
  frequency_label: string;
  /** The payout account as the client named it (bank and last four), or null. */
  payout_account: string | null;
  /** The signed First Repayment Date, beside the first debit (DOC-67 UI-DA-67-12). */
  signed_first_repayment_date: string | null;
}

/** DOC-67 / `FC-DET-087` — the 30 days from the client's signature to payout. */
interface KsPayoutClockDecision {
  short_id: string;
  reference: string;
  decision_text: string;
  outcome: 'APPROVE_PAYOUT' | 'END_AGREEMENT' | 'NO_PAYOUT';
  outcome_label: string;
  recorded_by: string;
  recorded_at: string;
}

export interface KsPayoutClock {
  signed_on: string | null;
  days: number | null;
  day_of: number;
  phase: 'RUNNING' | 'NEARING' | 'STOPPED' | null;
  line: string;
  payout_stopped: boolean;
  can_record_decision: boolean;
  notice_due: boolean;
  notice_task_line: string;
  ended: boolean;
  notice: {
    sent_on: string;
    sent_how: string;
    file: KsSigningFile;
    recorded_by: string;
    recorded_at: string;
  } | null;
  decisions: KsPayoutClockDecision[];
  outcomes: { code: KsPayoutClockDecision['outcome']; label: string }[];
}

export interface KsPayoutDecisionPayload {
  reference: string;
  decision_text: string;
  outcome: KsPayoutClockDecision['outcome'];
}

export interface KsBeforePayoutPanel {
  shown: boolean;
  rows: KsBeforePayoutRow[];
  lines: KsBeforePayoutLine[];
  activation: {
    available: boolean;
    blockers: KsActivationBlocker[];
    confirmation: KsActivationConfirmation | null;
  };
  payout: { state: 'PAID_OUT'; at: string | null; by: string } | null;
  clock: KsPayoutClock | null;
}

/** *Waive* (DOC-87 UI-DA-87-02): the condition pressed, the reason; scope and tier are the record's. */
export interface KsWaivePayload {
  entity: string;
  facility_type: 'KS';
  condition_id: string;
  kickstart_application?: string;
  reason_text: string;
}

export interface KsActivatePayload {
  note?: string;
  loan_agreement_ref?: string;
  debit_order_ref?: string;
  payout_account_ref?: string;
}

interface KsInstalment {
  sequence: number;
  due_date: string;
  amount: string;
}

/**
 * An activated Kickstart facility as the client sees it (FLOW-119).
 *
 * The charge is a **facility fee**, never interest — BRE-056 §3 makes it a
 * fixed fee struck once on the loan amount, and neither CapX product charges
 * interest. Do not rename these fields or label them otherwise in a template.
 *
 * N4: the internal pricing tier is deliberately absent. The client sees the
 * rate they are charged, not the tier label the system derived it from.
 */
export interface KsFacility {
  approved_amount: string;
  facility_fee_amount: string;
  /** Percentage, e.g. "28.00" — not a fraction. */
  fee_rate_applied: string;
  total_repayable: string;
  repayment_frequency: KsRepaymentFrequency;
  instalment_count: number;
  disbursed_at: string;
  /** Earliest instalment not yet past due; null once all have passed. */
  next_instalment: KsInstalment | null;
  instalments: KsInstalment[];
  /**
   * `§B9` — settlement month → % discount on the facility fee. **Empty once
   * `§B9.1`'s bar has fallen** (FLOW-178): the client is shown the consequence
   * it signed for, never the day, the instalment or the ground (N4).
   */
  early_settlement_discount_pct: Record<string, string>;
}

/**
 * FLOW-178 (`§B9.1`) — the early-settlement discount bar as the record holds
 * it: a latch written once (the day it fell, off which instalment) and every
 * written CapX agreement re-enabling the discount for one settlement date.
 * Ops-only (N4); null before payout. The screen works none of it out.
 */
interface KsEarlySettlementBar {
  /** The day the bar took effect — the due date plus seven calendar days. */
  barred_on: string;
  ground: 'INSTALMENT_UNPAID_7_DAYS';
  ground_display: string;
  instalment_sequence: number | null;
  recorded_at: string;
  /** Null when the system wrote the row — today, always. */
  recorded_by_name: string | null;
}

interface KsEarlySettlementOverride {
  short_id: string;
  /** The ONE settlement date the agreement re-enables the discount for. */
  settlement_on: string;
  written_agreement_reference: string;
  reason: string;
  recorded_by_name: string | null;
  recorded_at: string;
}

export interface KsEarlySettlement {
  /** False once the bar exists — an override is per date and never lifts it generally. */
  discount_available: boolean;
  bar: KsEarlySettlementBar | null;
  overrides: KsEarlySettlementOverride[];
}

export interface KsEarlySettlementOverridePayload {
  settlement_on: string;
  written_agreement_reference: string;
  reason: string;
}

/** FLOW-224 (DOC-78 UI-DA-78-02) — the figure as the record holds it, and its basis. */
export interface KsFirstLook {
  amount: string;
  bank_statement_months: number;
  financial_statements: boolean;
  /** ISO 8601 UTC — when the run that produced the figure read the file. */
  as_at: string;
}

export interface KsApplicationDetail extends KsApplication {
  documents: KsDocument[];
}

/**
 * `KickstartApplicationOpsSerializer` is not the client serializer plus extras —
 * it omits four client-shaped fields, so they are subtracted here rather than
 * inherited. Typing a field the payload never carries is worse than not typing
 * it: the compiler then vouches for an `undefined`.
 */
/** FLOW-319 — the five offer emails (DOC-66 §4), as NotificationLog categories. */
export type KsOfferEmailCategory =
  | 'KS_OFFER_READY'
  | 'KS_OFFER_CHANGED'
  | 'KS_OFFER_REMINDER'
  | 'KS_OFFER_LAPSED'
  | 'KS_OFFER_WITHDRAWN';

/** One send on the Ops offer panel (DOC-66 §8) — no open or click data. */
interface KsOfferEmail {
  category: KsOfferEmailCategory;
  version: number | null;
  sent_at: string;
  to_email: string;
  /** The mail server refused the send. A later bounce is not recorded. */
  failed: boolean;
}

export interface KsApplicationOps
  extends
    KsAssessmentFields,
    Omit<
      KsApplication,
      | 'dealt_with_by'
      | 'onboarding_progress'
      | 'client_info_request_message'
      | 'facility'
      | 'offer'
      | 'first_look'
      | 'client_context_open'
      | 'business_account_confirmation'
      | 'signing_status'
      // FLOW-326: the client page's outstanding list. FLOW-347 puts the Ops view
      // of it back, with each item's basis (KsAssessmentFields).
      | 'outstanding'
      | 'position'
      | 'tax_pins'
      | 'tax_pin_open'
      | 'stage_row'
    > {
  id: string;
  // FLOW-372 — the heading's stage; null on list rows.
  journey_stage: KsJourneyStage | null;
  // FLOW-402 (DOC-89-A §5) — the header's stage row; null on list rows.
  stage_row: KsStageRow | null;
  /**
   * FLOW-402 (DOC-89-A §4) — each folded card's one line, keyed by the card,
   * in the record's words (`kickstart/panel_lines.py`): an act, a hold or a
   * refusal named; the screen computes none of it. Null on list rows.
   */
  panel_lines: Record<string, string> | null;
  // FLOW-346 — who picked the file up; null offers *Pick up* on the Ops list.
  assignee: number | null;
  assignee_name: string;
  // FLOW-303/305 — the offer panel (DOC-62 §8.2).
  offers: KsOfferOps[];
  frequency_requests: KsFrequencyRequest[];
  cadence_referrals: KsCadenceReferral[];
  activation_blockers: KsActivationBlocker[];
  /** FLOW-366 — the business still owes its own account confirmation. */
  business_account_confirmation_owed: boolean;
  /**
   * FLOW-352 (DOC-83 §3) — the tax line's PIN: whether the business still owes
   * it, and every PIN sent, newest first. Detail only; null on a list row.
   */
  tax_pin: { owed: boolean; pins: KsTaxPinOps[] } | null;
  // FLOW-351 (DOC-82): the Amount section's record (detail only; null on a
  // list row), every referral to the Credit Committee newest first, and the
  // ground a decline recorded.
  amount_review: KsAmountReview | null;
  committee_referrals: KsCommitteeReferral[];
  decline_ground: KsDeclineGround | '';
  // FLOW-319 — every offer email sent, newest first (DOC-66 §8).
  offer_emails: KsOfferEmail[];
  // BRE-055 document lane — system-set + submit-locked; Ops-only (the client
  // serializer omits it). See kickstart serializers (FLOW-64).
  application_path: string | null;
  // BRE-055 LOW_DATA_PROFILE (FLOW-162) — TRUE on the BANK_ONLY lane, the
  // restricted track. Mirrors application_path; Ops-only for the same reason.
  low_data_profile: boolean;
  // BRE-200 KS-OB-02a/02b coverage + the §6.1 90-day staleness clock.
  // Ops-only ("System + Ops", §2.1) — never on the client serializer, so a
  // client cannot self-certify the coverage that gates its own application.
  // `bank_data_accepted_at` null means never assessed, which is a different
  // fact from a count of zero (assessed, and it covered nothing).
  bank_statements_accepted_count: number;
  bank_data_accepted_at: string | null;
  // Entity verification state — Ops-only, and the same four flags the DF
  // facility carries (`DfFacility`), where they are client-visible.
  cipc_verified: boolean;
  kyc_verified: boolean;
  sars_verified: boolean;
  bank_account_ownership_verified: boolean;
  trading_history_months: number | null;
  monthly_turnover_avg: string | null;
  monthly_expenses_avg: string | null;
  surplus_cash: string | null;
  overdraft_amount: string | null;
  director_credit_score: number | null;
  entity_type: string | null;
  ks_matter_status: string | null;
  ks_matter_status_display: string | null;
  // FLOW-178 (`§B9.1`): the early-settlement discount bar and its overrides,
  // as the record holds them. Null before payout. Ops-only (N4).
  early_settlement: KsEarlySettlement | null;
  /** Ops repayment screen (FLOW-124): null unless ACTIVE. Ops-only (N4). */
  repayment_ledger: KsRepaymentLedger | null;
  base_safe_exposure: string | null;
  system_recommended_amount: string | null;
  pricing_tier: string | null;
  facility_fee_rate: string | null;
  // FLOW-236 (FC-DET-017/019): the explicit sizing state and the engine's own
  // sentence for it. `sizing_reason` is non-empty exactly on REFUSED and
  // NOT_VIABLE — on NOT_VIABLE it is the decline-or-refer sentence, the
  // operative content of the state. The marker+flags pair travels with them.
  sizing_state: KsSizingState;
  sizing_reason: string;
  sizing_mandatory_flags: string[] | null;
  sizing_calculated_at: string | null;
  // FLOW-213 (BRE-SPEC-FIO v1.0): every funder the latest scored run detected,
  // beside its two §B4.1a records. Empty when no run with a payload exists or
  // the scan found nothing. Ops-only (N4).
  funder_investigations: KsFunderInvestigation[];
  /**
   * FC-DET-037 (FLOW-259): what the latest scored run says about the §3 funder
   * scan itself. An empty `funder_investigations` means either "no scan" or "a
   * clean scan" — this says which. Ops-only (N4).
   */
  funder_scan: KsFunderScanState;
  /**
   * FLOW-261 (§A11.8 U9 limb (2)): the completed records on this application that
   * NO detection of the latest scored run matched. A lost record must be visible
   * to the reviewer; a row's `security_record: null` stays the never-made case
   * only because this list sits beside it. Ops-only (N4).
   */
  unmatched_funder_records: KsUnmatchedFunderRecord[];
  /**
   * FLOW-309 (`FC-DET-068`): the latest scored run's funder FLAGS, as recorded.
   * 🚨 `null` = no assessment has been run; `[]` = one ran and flagged nothing.
   * Opposite facts — the panel renders them differently. Ops-only (N4).
   */
  funder_flags: KsFunderFlag[] | null;
  /** The lookup-list versions that run recorded (`UI-DA-63-12`); `null` with no run. */
  lookup_list_versions: Partial<LookupListVersions> | null;
  /** When that run was made; `null` with no run. */
  funder_flags_assessed_at: string | null;
  /**
   * FLOW-313 (`FC-DET-074`/`075`; DOC-63 §2–§3, DOC-63-A §2–§4): the Ops funder
   * task — on every application, `OUTSTANDING` until an answer is on record.
   * Answers newest first. Ops-only (N4).
   */
  funder_task: KsFunderTask;
  /**
   * The open declared-account hold (FLOW-277), or `null` when nothing is held.
   *
   * 🚨 **PRE-SIZING (`DOC-58` `UI-DA-58-02`): while this is set, no facility
   * size exists** — render `SizingHoldCardComponent` and no size element.
   */
  sizing_hold: SizingHold | null;
  /**
   * Whether the client has affirmed its account list is COMPLETE (FLOW-284).
   *
   * 🔑 **Read beside `sizing_hold`: it decides whether the release ACTION is
   * offered.** Limb (a) is re-read on every release, so releasing a
   * `DECLARED_ACCOUNTS_NOT_ON_FILE` hold while this is `false` raises an
   * identical hold seconds later — and the API now refuses it with a 409.
   * A fact about the ENTITY, so it is `false` on a file with no hold too.
   */
  affirmation_in_force: boolean;
  /**
   * FLOW-288 (`§B1`) — CapX's elections on the failed-debit charge, newest first.
   *
   * 🚨 **Evidence of an act CAPX took, never a finding about the client.** It is
   * rendered as CapX's own record and must not appear among the client's risk
   * attributes, nor in any client payload (N4). Empty until an election is made,
   * and never empty by expiry: nothing removes or hides a row once written.
   */
  failed_debit_elections: KsFailedDebitElection[];
  trust_index_score: string | null;
  ks_recovery_signal: string | null;
  /** DOC-52: cross-facility KS recovery flag; null when KS_CLEAR (render nothing). */
  ks_recovery_flag: KsRecoveryFlag | null;
  // Denormalised enum mirror (APPROVE / DECLINE / MORE_INFO / "" when unassessed),
  // NOT the rich AiRecommendation object — see kickstart serializer "AI recommendation mirror".
  latest_ai_recommendation: string | null;
  // Full persisted advisory (rationale, confidence, flags) — null when unassessed.
  latest_ai_advisory: LatestAiAdvisory | null;
  latest_ai_attempt: LatestAiAttempt | null;
  risk_summary: RiskSummary;
  decided_at: string | null;
  disbursed_at: string | null;
  loan_agreement_ref: string | null;
  debit_order_ref: string | null;
  payout_account_ref: string | null;
  // KS-OB-06/07/08 actors (FLOW-163). The `_at` companions are inherited from
  // the client shape; only the `_by` actor is Ops-only.
  loan_agreement_signed_by: string | null;
  debit_order_confirmed_by: string | null;
  payout_account_confirmed_by: string | null;
  updated_at: string;
  status_events: KsStatusEvent[];
  review_notes: KsReviewNote[];
  /** FLOW-327 (DOC-74): CapX's entries, the earlier review notes included, newest first. */
  ops_context: KsOpsContextEntry[];
  /**
   * FLOW-327 (DOC-58): why no offer can be issued for want of a figure — the
   * back end's sentence, '' when a figure exists. The approve control is
   * absent while it is set.
   */
  approval_wait: string;
  /** DOC-92 UI-DA-92-05: shown in *Run the assessment*'s place; '' when offered. */
  bureau_check_wait: string;
  documents: KsDocument[];
}

// ---------------------------------------------------------------------------
// Response / payload shapes
// ---------------------------------------------------------------------------

export interface KsApplicationsResponse {
  applications: KsApplication[];
}

export interface KsOpsApplicationsResponse {
  applications: KsApplicationOps[];
}

export interface KsSubmitResponse {
  message: string;
  application: KsApplicationDetail;
}

export interface CreateKsPayload {
  entity: string; // entity short_id
  amount_requested: string;
  purpose?: string;
  // No application_path: it is system-determined and locked at submit, not a
  // create-time client choice (BRE-055, FLOW-64).
  /** DOC-72 step 1 (FLOW-331) — the form's answer, never the route. */
  has_financials_declared?: boolean | null;
}

export interface CaptureKsPayload {
  // Applicant-writable capture fields only. The BRE-200 verification gates and
  // application_path (CapX's internal pricing tier) are set back-office, never by
  // the client — the API rejects them on this endpoint.
  amount_requested?: string;
  purpose?: string;
  has_financials_declared?: boolean | null;
  trading_history_months?: number | null;
  monthly_turnover_avg?: string;
  monthly_expenses_avg?: string | null;
  surplus_cash?: string | null;
  overdraft_amount?: string | null;
  director_credit_score?: number | null;
  /** §B11.1g — a preference, never a choice (FLOW-305). */
  repayment_frequency_preference?: KsRepaymentFrequency | '';
}

export interface KsDecisionPayload {
  action: KsDecisionAction;
  note?: string;
  message?: string;
  /** Required on 'approve' = issue the offer (DOC-54 §3 Confirm/Reduce/Exceed). */
  approved_amount?: string;
  /** FLOW-351: the written reason — required on 'refer' and on 'approve'. */
  reason?: string;
  /** FLOW-351: which of DOC-82 §5's three endings a 'decline' records. */
  ground?: KsDeclineGround;
}

/**
 * The 202 from `POST ops/applications/<id>/assess/`.
 *
 * It used to carry the verdict, because the endpoint ran the assessment inline.
 * It no longer does: the run takes minutes and outlived the worker timeout, so the
 * result arrives via the detail endpoint instead (FLOW-211).
 */
export interface KsAssessResponse {
  status: 'QUEUED';
  detail: string;
}

// ---------------------------------------------------------------------------
// Status metadata maps
// ---------------------------------------------------------------------------

export const KS_STATUS_META: Record<KsStatus, StatusMeta> = {
  NONE: { label: 'Not started', variant: 'neutral' },
  PENDING: { label: 'Pending', variant: 'warning' },
  ACTIVE: { label: 'Active', variant: 'success' },
  REPAID: { label: 'Repaid', variant: 'default' },
};

export const KS_STAGE_META: Record<KsApplicationStage, StatusMeta> = {
  // Amber, not grey: a draft is the one stage that needs the applicant to act
  // (finish + submit), so it must read as "unfinished", not "dormant".
  DRAFT: { label: 'Draft', variant: 'warning' },
  SUBMITTED: { label: 'Submitted', variant: 'default' },
  MORE_INFO_REQUIRED: { label: 'More info required', variant: 'warning' },
  DECLINED: { label: 'Declined', variant: 'destructive' },
};

/**
 * FLOW-372 (DOC-62 §3): the stage the Ops heading reads — the back end's
 * `journey_stage`, which is `OFFER_OPEN` while the current offer stands open and
 * otherwise `application_stage`. The screen works out no stage of its own.
 */
type KsJourneyStage = KsApplicationStage | 'OFFER_OPEN';

export const KS_JOURNEY_STAGE_META: Record<KsJourneyStage, StatusMeta> = {
  ...KS_STAGE_META,
  OFFER_OPEN: { label: 'Offer open', variant: 'success' },
};

type KsSizingState = 'NOT_ASSESSED' | 'REFUSED' | 'NOT_VIABLE' | 'SIZED';

/**
 * FC-DET-019 (Constitution v1.98): the four sizing outcomes. REFUSED means the
 * engine could not answer; NOT_VIABLE means it answered, and the answer is
 * decline or refer — collapsing the two throws away a decision the system
 * already made, which is why NOT_VIABLE is the harsher variant of the pair.
 */
export const KS_SIZING_STATE_META: Record<KsSizingState, StatusMeta> = {
  NOT_ASSESSED: { label: 'Not assessed', variant: 'neutral' },
  REFUSED: { label: 'Refused', variant: 'warning' },
  NOT_VIABLE: { label: 'Not viable', variant: 'destructive' },
  SIZED: { label: 'Sized', variant: 'success' },
};

/**
 * FLOW-213 — the §B4.1a/§B4.1b funder investigation's two records
 * (BRE-SPEC-FIO v1.0, N1-approved 2026-09-07).
 *
 * SECURITY answers "is the debtors book ceded to this funder, and was any
 * competing cession released?" — a state, never a boolean. TREATMENT answers
 * "what does this funder become in the sizing arithmetic?" — a commercial
 * election, not derived from security. A missing record is `null`: unresolved
 * is the ABSENCE of a record, there is no PENDING member, and the screen must
 * show "no record" rather than a state.
 */
export type KsFunderSecurityState = 'NOT_CEDED' | 'CEDED_RELEASED' | 'CEDED_LIVE';
export type KsFunderTreatmentState = 'SETTLED_FROM_PROCEEDS' | 'REMAINS_IN_PLACE';

interface KsFunderRecord<S extends string> {
  state: S;
  actor_name: string;
  recorded_at: string;
  note: string;
  /** FLOW-314 — on a security record under a declared funder only. */
  confirmation_method?: string;
  confirmation_source?: KsReleaseSource | '';
  confirmation_source_detail?: string;
  follows_client_settlement?: boolean;
  funder_paid_confirmed_at?: string | null;
}

/** From whom CapX received confirmation of a release — never the client (FC-DET-075). */
export type KsReleaseSource = 'FUNDER_DIRECTLY' | 'INDEPENDENT_SOURCE';

/** FLOW-314 — CapX's own confirmation, which a CEDED_RELEASED record needs. */
export interface KsReleaseConfirmation {
  confirmation_method: string;
  confirmation_source: KsReleaseSource | '';
  confirmation_source_detail: string;
  follows_client_settlement: boolean;
  funder_paid_confirmed: boolean;
}

export type KsSecurityAnswer = 'YES' | 'NO' | 'CANNOT_SAY';
export type KsFunderEntryKind = 'DEBTOR_FINANCE' | 'OTHER_LOAN';

/** One declared funder in a recorded answer, with its records where it opened them. */
export interface KsFunderAnswerEntry {
  declaration: string;
  funder_name: string;
  kind: KsFunderEntryKind;
  holds_security_over_debtors_book: KsSecurityAnswer;
  security_answer_words: string;
  /** True only on the CURRENT answer's yes / cannot-say entries. */
  security_record_open: boolean;
  security_record: KsFunderRecord<KsFunderSecurityState> | null;
  treatment_record: KsFunderRecord<KsFunderTreatmentState> | null;
}

interface KsFunderAnswer {
  answer: 'NO' | 'DECLARED';
  capture_route: 'TAKEN_BY_OPS';
  actor_name: string;
  taken_at: string;
  entries: KsFunderAnswerEntry[];
}

export interface KsFunderTask {
  status: 'OUTSTANDING' | 'ANSWERED';
  answers: KsFunderAnswer[];
}

export interface KsFunderAnswerPayload {
  answer: 'NO' | 'DECLARED';
  entries: {
    funder_name: string;
    kind: KsFunderEntryKind;
    holds_security_over_debtors_book: KsSecurityAnswer;
    security_answer_words: string;
  }[];
}

type KsFunderScanState = 'NOT_RUN' | 'CLEAN' | 'DETECTED';

/**
 * One funder flag off `SizingRun.inputs.funder_flags` (FLOW-308/309) — information
 * for the Ops funder question, never a block. `LOOKUP`: a known-lenders name on a
 * receipt (`entry_kind`, `date`, `amount`, `description`). `MODEL_SCAN`: a party
 * the scan named without evidence (`evidence`, `first_date`, `amount` — the row
 * it cited). A run from before FLOW-309 carries only the first four keys.
 */
export interface KsFunderFlag {
  counterparty: string;
  source: 'LOOKUP' | 'MODEL_SCAN';
  trigger: string;
  reason: string;
  entry_kind?: 'BANK' | 'SPECIALIST_LENDER' | null;
  date?: string | null;
  description?: string | null;
  evidence?: string | null;
  first_date?: string | null;
  amount?: string | null;
}

/** One `§B1` election CapX made on the failed-debit charge (FLOW-288). */
export interface KsFailedDebitElection {
  election: KsFailedDebitElectionChoice;
  /** The server's own label for the choice — never re-worded in a template. */
  election_display: string;
  /** Operations' reasoning. Required by `§B1`, so never empty on a stored row. */
  basis: string;
  acting_person: string;
  acting_person_email: string;
  recorded_at: string;
  note: string;
}

export type KsFailedDebitElectionChoice = 'CHARGE' | 'WAIVE';

/** POST body for an election. `acting_person` is the request's user, never sent. */
export interface KsFailedDebitElectionPayload {
  election: KsFailedDebitElectionChoice;
  basis: string;
  note?: string;
}

export interface KsFunderInvestigation {
  counterparty: string;
  /** The §3 detection classification — upstream of both records, untouched by them. */
  classification: string;
  trigger: string | null;
  amount: string | null;
  evidence: string | null;
  first_date: string | null;
  last_date: string | null;
  /**
   * FLOW-260 — the receipt the records are anchored to (§A11 U8: date and amount),
   * `'<ISO date>|<amount>'`; null when the detection's evidence yielded no parseable
   * pair, in which case `matched_by` is 'NAME' and `evidence_flag` says so. Echoed
   * back on a write so it lands on ONE receipt — never typed.
   */
  evidence_key: string | null;
  evidence_date: string | null;
  evidence_amount: string | null;
  matched_by: 'EVIDENCE' | 'NAME';
  evidence_flag: string | null;
  security_record: KsFunderRecord<KsFunderSecurityState> | null;
  treatment_record: KsFunderRecord<KsFunderTreatmentState> | null;
  /** Both records exist and the security one clears §B4.1a. */
  cleared: boolean;
  /** What this row does to sizing today. */
  blocks_sizing: boolean;
}

/**
 * FLOW-261 — §A11.8 U9 limb (2). Why a completed record did not match the current
 * assessment: `NO_DETECTION_CITES_RECEIPT` — the row is keyed on a receipt (date
 * and amount) no current detection derives; `NAME_KEYED_ROW_NOT_CONSULTED` — the
 * row has no receipt key (the backfill could not derive one and kept it on the
 * funder's name), and a detection that carries a receipt key never consults the
 * name. Neither is a state and neither clears anything.
 */
type KsUnmatchedFunderReason = 'NO_DETECTION_CITES_RECEIPT' | 'NAME_KEYED_ROW_NOT_CONSULTED';

export interface KsUnmatchedFunderRecord {
  table: 'SECURITY' | 'TREATMENT';
  counterparty: string;
  /** The receipt the row was filed against, `'<ISO date>|<amount>'`; null on a name-keyed row. */
  evidence_key: string | null;
  counterparty_key: string;
  state: KsFunderSecurityState | KsFunderTreatmentState;
  actor_name: string;
  recorded_at: string;
  reason: KsUnmatchedFunderReason;
}

/** One line per reason, shown under the lost record. */
export const KS_UNMATCHED_FUNDER_REASON_COPY: Record<KsUnmatchedFunderReason, string> = {
  NO_DETECTION_CITES_RECEIPT:
    'No detection in the current assessment cites this receipt — the record was filed against a ' +
    'date and amount this run did not read.',
  NAME_KEYED_ROW_NOT_CONSULTED:
    "Recorded against the funder's name only, with no receipt key — a detection that carries a " +
    'receipt key never consults the name, so this record cannot be matched.',
};

export const KS_UNMATCHED_FUNDER_TABLE_LABEL: Record<KsUnmatchedFunderRecord['table'], string> = {
  SECURITY: 'Security record',
  TREATMENT: 'Treatment record',
};

export interface KsFunderRecordPayload extends Partial<KsReleaseConfirmation> {
  /** A detected funder — or, FLOW-313, `declaration` for a declared one. Exactly one. */
  counterparty?: string;
  declaration?: string;
  state: string;
  note?: string;
  /** The panel row's own `evidence_key`, echoed back (FLOW-260). */
  evidence_key?: string | null;
}

export const KS_FUNDER_SECURITY_META: Record<KsFunderSecurityState, StatusMeta> = {
  NOT_CEDED: { label: 'Not ceded', variant: 'success' },
  CEDED_RELEASED: { label: 'Ceded — released', variant: 'success' },
  CEDED_LIVE: { label: 'Ceded — live', variant: 'destructive' },
};

export const KS_FUNDER_TREATMENT_META: Record<KsFunderTreatmentState, StatusMeta> = {
  SETTLED_FROM_PROCEEDS: { label: 'Settled from proceeds', variant: 'default' },
  REMAINS_IN_PLACE: { label: 'Remains in place', variant: 'default' },
};

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Signing — the client-visible part (FLOW-367, DOC-68-B). The Ops panels,
// receipt decisions and *What the business submitted* live in
// kickstart-ops.types.ts.
// ---------------------------------------------------------------------------

export type KsSigningRoute = 'SIGNIFLOW' | 'EMAIL' | 'BY_HAND';

/** The client's view — no reason, no name, no file (N4). */
interface KsSigningStatus {
  state: 'PREPARING' | 'SENT' | 'SIGNED';
  sent_on: string | null;
  route: KsSigningRoute | null;
  signed_on: string | null;
}

export interface KsSigningFile {
  id: number;
  name: string;
}
